import { useState } from "react";
import { DataType } from "../../../../types/constant";

import IncludedFeaturesForm from "./IncludedFeaturesForm";
import { saveIncludedFeature } from "../../../../services/includedFeatureService";
import PageContainer from "../../PageContainer";
import { Stack, Tooltip, IconButton } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useData } from "../../../../contexts/DataContext/DataContextt";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { usePreload } from "../../../../contexts/DataContext/PreloadContext";
import { keepOnlyCurrentLanguage } from "../../../../helpers/EntityHelper";
import { includedFeaturePageCache } from "../../../../pages/cache/PageCache";
import usePageIncludedFeature from "../../../../contexts/hooks/usePageIncludedFeature";

export default function EditIncludedFeatures() {
  const [updating, setUpdating] = useState(false);
  const { loading, language } = useData();
  const { showNotification } = usePreload();
  const location = useLocation();
  const navigate = useNavigate();
  const { classCode } = useParams();
  const row = location.state?.row;
  const id = row?.includedFeatureClassCode;
  const { upsertCache } = usePageIncludedFeature({
    id: id,
    language,
    deps: [id, language],
  });

  // The row only arrives via router state from the included features list;
  // on a direct visit or refresh, send the user back to that list.
  if (!row) {
    return <Navigate to={`/classes/${classCode}/includedfeatures`} replace />;
  }

  const onUpdate = async (updatedRow, currentRow) => {
    try {
      setUpdating(true);

      let newIncludedFeature = keepOnlyCurrentLanguage(updatedRow, language);

      const notDefaultIncludedFeatureValues =
        !newIncludedFeature.includedFeatureValuesTemporary
          ? []
          : newIncludedFeature.includedFeatureValuesTemporary
              .split("|")
              .filter((id) => {
                const found = currentRow.values?.find((f) => f.id === id);
                return !found || !found.isDefault;
              });

      const notDefaultIncludedFeatureUnit =
        !newIncludedFeature.includedFeatureUnitCodeTemporary
          ? []
          : newIncludedFeature.includedFeatureUnitCodeTemporary
              .split("|")
              .filter((id) => {
                const found = currentRow.unit?.find((f) => f.id === id);
                return !found || !found.isDefault;
              });

      const isNumeric =
        currentRow.featureType === DataType.RANGE ||
        currentRow.featureType === DataType.NUMERIC ||
        currentRow.featureType === DataType.STRING;
      const isAlphaNumeric = currentRow.featureType === DataType.ALPHANUMERIC;

      newIncludedFeature = {
        ...newIncludedFeature,
        ...(isAlphaNumeric && {
          includedFeatureValues: notDefaultIncludedFeatureValues
            .filter((v) => v)
            .join("|"),
        }),
        ...(isNumeric && {
          includedFeatureUnitCode: notDefaultIncludedFeatureUnit
            .filter((v) => v)
            .join("|"),
        }),
        isSelectionNew: false,
      };

      await saveIncludedFeature(newIncludedFeature);
      upsertCache(newIncludedFeature);

      showNotification(
        `${newIncludedFeature?.includedFeatureUniqueId} was updated!`,
        "success",
      );
    } catch (e) {
      console.error(e);
      showNotification(
        `something went wrong updating the ${updatedRow?.includedFeatureUniqueId} included feature.`,
        "error",
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <PageContainer
      title="Edit Included Feature"
      breadcrumbs={[
        { title: `ETIM Class` },
        {
          title: `${row?.includedFeatureClassCode}`,
        },

        {
          title: `IncludedFeatures`,
          path: `/classes/${row?.includedFeatureClassCode}/includedfeatures`,
        },
        {
          title: `${row?.includedFeatureFeatureId}`,
        },
        { title: "Edit" },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            {
              <div>
                <IconButton
                  size="small"
                  aria-label="refresh"
                  onClick={() => {
                    navigate(
                      `/classes/${row?.includedFeatureClassCode}/includedfeatures`,
                    );
                  }}
                >
                  <KeyboardBackspaceIcon />
                </IconButton>
              </div>
            }
          </Tooltip>
        </Stack>
      }
    >
      <IncludedFeaturesForm
        onSave={onUpdate}
        props={{
          row: row,
          loading: loading,
          uploading: updating,
          showCancel: false,
          saveText: "Save Changes",
          isUpdate: true,
        }}
      />
    </PageContainer>
  );
}
